import { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { fetchInvoiceQueue } from '../services';
import QueueDrawer from '../components/QueueDrawer';
import { getDummyInvoiceQueue } from '../data/dummyDashboardData';
import { isTestEnvironment } from '../utils/isTestEnvironment';

const QUEUE_COMPANY_IDS = [8, 303];

function formatDateOnly(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatAmount(amount) {
  return parseFloat(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCurrency(amount) {
  return `$${formatAmount(amount)}`;
}

function getQueueItemAmount(item) {
  return (item.line_items ?? []).reduce(
    (sum, li) => sum + (parseFloat(li.Amount) || 0),
    0,
  );
}

function getStatusBadgeClass(status) {
  if (status === 'pending') return 'status-badge--pending';
  if (status === 'completed' || status === 'processed') return 'status-badge--success';
  if (status === 'failed' || status === 'error') return 'status-badge--error';
  return 'status-badge--pending';
}

export const QueuePage = () => {
  const { token, base_url, company } = useAppContext();
  const [queueItems, setQueueItems] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);

  const canAccessQueue = QUEUE_COMPANY_IDS.includes(company?.company_id) || isTestEnvironment();

  const loadQueue = useCallback(async () => {
    if (!token || !base_url) return;
    setLoading(true);
    try {
      const data = await fetchInvoiceQueue(token, base_url);
      const apiItems = data.invoices_queue || [];
      if (apiItems.length === 0 && isTestEnvironment()) {
        const dummy = getDummyInvoiceQueue();
        setQueueItems(dummy.invoices_queue);
        setMetadata(dummy.metadata);
      } else {
        setQueueItems(apiItems);
        setMetadata(data.metadata || null);
      }
    } catch (error) {
      console.error('Error loading invoice queue:', error);
      if (isTestEnvironment()) {
        const dummy = getDummyInvoiceQueue();
        setQueueItems(dummy.invoices_queue);
        setMetadata(dummy.metadata);
      }
    } finally {
      setLoading(false);
    }
  }, [token, base_url]);

  useEffect(() => {
    if (canAccessQueue) {
      loadQueue();
    }
  }, [loadQueue, canAccessQueue]);

  const companyName = company?.company_name || company?.name || 'Your company';

  const statuses = useMemo(
    () => ['all', ...Array.from(new Set(queueItems.map(item => item.status).filter(Boolean)))],
    [queueItems],
  );

  const filtered = useMemo(
    () => queueItems.filter(item => statusFilter === 'all' || item.status === statusFilter),
    [queueItems, statusFilter],
  );

  const pendingCount = useMemo(
    () => queueItems.filter(item => item.status === 'pending').length,
    [queueItems],
  );

  const totalAmount = useMemo(
    () => queueItems.reduce((sum, item) => sum + getQueueItemAmount(item), 0),
    [queueItems],
  );

  if (!canAccessQueue) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <div className="dashboard-wrapper">
        <div className="dashboard-page">
          <header className="dashboard-header">
            <div className="dashboard-header-main">
              <div className="dashboard-header-title-row">
                <div>
                  <h1 className="dashboard-title">Invoice Queue</h1>
                  <p className="dashboard-subtitle">{companyName}</p>
                </div>
                <div className="dashboard-header-stats">
                  <div className="dashboard-header-stat dashboard-invoice-stat">
                    <span className="dashboard-header-stat-label">Queued Invoices</span>
                    <span className="dashboard-header-stat-value">
                      {loading ? '…' : (metadata?.total_count ?? queueItems.length)}
                    </span>
                  </div>
                  <div className="dashboard-header-stat dashboard-time-savings-stat">
                    <span className="dashboard-header-stat-label">Total Value</span>
                    <span className="dashboard-header-stat-value">
                      {loading ? '…' : formatCurrency(totalAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="dashboard-header-divider" role="separator" />

          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-value">{loading ? '…' : queueItems.length}</span>
              <span className="stat-label">Total Queued</span>
            </div>
            <div className="stat-card stat-card--success">
              <span className="stat-value">{loading ? '…' : pendingCount}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{loading ? '…' : formatCurrency(totalAmount)}</span>
              <span className="stat-label">Queue Value</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{loading ? '…' : filtered.length}</span>
              <span className="stat-label">Showing</span>
            </div>
          </div>

          <div className="dashboard-filter-bar">
            {statuses.map(status => (
              <button
                key={status}
                type="button"
                className={`filter-btn${statusFilter === status ? ' active' : ''}`}
                onClick={() => setStatusFilter(status)}
              >
                {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                {status === 'pending' && (
                  <span className="filter-count">{pendingCount}</span>
                )}
              </button>
            ))}
          </div>

          <div className="event-list queue-list">
            <div className="event-list-header queue-list-header">
              <span>Queue Item</span>
              <span>Customer / Deal</span>
              <span>Term</span>
              <span>Amount</span>
              <span>Status</span>
              <span>Scheduled</span>
              <span></span>
            </div>

            {loading && (
              <div className="event-list-empty">Loading queue…</div>
            )}

            {!loading && filtered.length === 0 && (
              <div className="event-list-empty">
                {queueItems.length === 0
                  ? 'No invoices in the queue.'
                  : 'No items match this filter.'}
              </div>
            )}

            {!loading && filtered.map(item => {
              const isSelected = selectedItem?.id === item.id;
              const customer = item.invoice_data?.customer_name ?? '—';
              const deal = item.invoice_data?.deal_name ?? item.sf_opportunity?.Name ?? '—';
              const termYear = item.invoice_data?.term_year;
              const amount = getQueueItemAmount(item);

              return (
                <div key={item.id} className={`event-item${isSelected ? ' selected' : ''}`}>
                  <div className="event-row queue-row" onClick={() => setSelectedItem(item)}>
                    <div className="event-col-id">
                      <span className="event-id-badge">#{item.id}</span>
                      <span className="event-type">{item.process_type}</span>
                    </div>

                    <div className="event-col-deal">
                      <span className="event-company">{customer}</span>
                      <span className="event-deal-name">{deal}</span>
                    </div>

                    <div className="event-col-term">
                      {termYear != null
                        ? <span className="event-invoice">Year {termYear}</span>
                        : <span className="event-na">—</span>
                      }
                    </div>

                    <div className="event-col-amount">
                      {amount > 0
                        ? <span className="event-invoice">{formatCurrency(amount)}</span>
                        : <span className="event-na">—</span>
                      }
                    </div>

                    <div className="event-col-status">
                      <span className={`status-badge ${getStatusBadgeClass(item.status)}`}>
                        {item.status}
                      </span>
                    </div>

                    <div className="event-col-date">{formatDateOnly(item.future_date)}</div>

                    <div className="event-col-chevron">
                      <span className="event-chevron">›</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <QueueDrawer item={selectedItem} onClose={() => setSelectedItem(null)} />
    </>
  );
};
